import { z } from "zod";
import {
  CanonicalizationOutcomeSchema,
  CrawlRunStatusSchema,
} from "../../enums/core";

export const CrawlRunStatusValueSchema = CrawlRunStatusSchema;

export type CrawlRunStatusValue = z.infer<typeof CrawlRunStatusValueSchema>;

export const CreateCrawlRunResponseSchema = z.object({
  crawlRuns: z.array(
    z.object({
      crawlRunId: z.uuid(),
      websiteId: z.uuid(),
      status: CrawlRunStatusValueSchema,
    }),
  ),
});

export type CreateCrawlRunResponse = z.infer<
  typeof CreateCrawlRunResponseSchema
>;

// The repaired run keeps its id: a repair reopens a run, it never mints one.
export const RepairRunResponseSchema = z.object({
  crawlRunId: z.uuid(),
  websiteId: z.uuid(),
  status: z.literal("repairing"),
  repairedPageCount: z.number().int().positive(),
  promptBundleId: z.uuid(),
});

export type RepairRunResponse = z.infer<typeof RepairRunResponseSchema>;

export const CrawlFrontierResponseSchema = z.object({
  admitted: z.array(
    z.object({
      url: z.url(),
      depth: z.number().int().min(0),
    }),
  ),
});

export type CrawlFrontierResponse = z.infer<typeof CrawlFrontierResponseSchema>;

export const CrawlOkResponseSchema = z.object({
  ok: z.literal(true),
});

export type CrawlOkResponse = z.infer<typeof CrawlOkResponseSchema>;

// What actually happened to a completion request (silent deferral hid a
// zombie run once — the caller must be able to tell). `applied` flipped the
// run; `deferred` means frontier work is still pending/in_flight and the run
// stayed running; `canonicalizing` means the frontier drained but findings
// still need canonical problem keys, so materialization waits on the
// canonicalize worker; `noop` means the run was already in the requested state.
export const CrawlCompleteResponseSchema = z.object({
  outcome: CanonicalizationOutcomeSchema,
});

export type CrawlCompleteResponse = z.infer<typeof CrawlCompleteResponseSchema>;

// One-transaction operator abort of a running crawl: every outstanding
// frontier row and running yeet attempt is failed and the run flips to
// failed, so queued messages die on their next backend call (4xx, no requeue).
export const CrawlAbortResponseSchema = z.object({
  frontierFailed: z.number().int().min(0),
  attemptsFailed: z.number().int().min(0),
});

export type CrawlAbortResponse = z.infer<typeof CrawlAbortResponseSchema>;
